// Underwriting tab top-level component. Hosts
// the 9-section vertical accordion + drives the gate-state computation.
//
// Default-expanded section is the first incomplete one (computed via
// useUnderwritingTabState). Locked sections are visually disabled with a
// tooltip explaining the unmet gate.

import type { ReactElement } from "react";
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Lock } from "lucide-react";
import { SectionStatusBadge, type SectionStatus } from "../common/SectionStatusBadge";
import { PreUnderwritingApprovalSection } from "./sections/PreUnderwritingApprovalSection";
import { LoanDocumentIssuanceSection } from "./sections/LoanDocumentIssuanceSection";
import { EscrowSetupSection } from "./sections/EscrowSetupSection";
import { UnderwritingTeamAppointmentSection } from "./sections/UnderwritingTeamAppointmentSection";
import { CriteriaChecklistSection } from "./sections/CriteriaChecklistSection";
import { LanePassFailSection } from "./sections/LanePassFailSection";
import { VerificationOfAnalystWorkSection } from "./sections/VerificationOfAnalystWorkSection";
import { ApprovalRejectionLetterSection } from "./sections/ApprovalRejectionLetterSection";
import { ClosingCoordinatorAssignmentSection } from "./sections/ClosingCoordinatorAssignmentSection";
import { useUnderwritingTabState } from "./useUnderwritingTabState";
import type { DealDetailDto } from "@/types/deals";

interface UnderwritingTabContentProps {
  deal: DealDetailDto;
}

interface SectionMeta {
  num: number;
  title: string;
  /** Body component lazily rendered when expanded. */
  body: () => ReactElement;
}

export function UnderwritingTabContent({ deal }: UnderwritingTabContentProps) {
  const state = useUnderwritingTabState(deal.id);
  const [openSection, setOpenSection] = useState<number>(1);

  // Once gate state resolves, auto-expand the first incomplete section.
  useEffect(() => {
    if (!state.isLoading) {
      setOpenSection(state.firstIncompleteSection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isLoading, state.firstIncompleteSection]);

  const sections: SectionMeta[] = [
    {
      num: 1,
      title: "Pre-Underwriting Approval",
      body: () => <PreUnderwritingApprovalSection deal={deal} />,
    },
    {
      num: 2,
      title: "Escrow Setup",
      body: () => <EscrowSetupSection deal={deal} />,
    },
    {
      num: 3,
      title: "Underwriting Team Appointment",
      body: () => (
        <UnderwritingTeamAppointmentSection dealId={deal.id} dealStage={deal.stage} />
      ),
    },
    {
      num: 4,
      title: "A1 Criteria Checklist",
      body: () => <CriteriaChecklistSection dealId={deal.id} lane="A1" />,
    },
    {
      num: 5,
      title: "A1 Pass / Fail + DQR",
      body: () => <LanePassFailSection dealId={deal.id} lane="A1" />,
    },
    {
      num: 6,
      title: "Verification of Analyst Work",
      body: () => <VerificationOfAnalystWorkSection dealId={deal.id} />,
    },
    {
      num: 7,
      title: "Loan Document Issuance",
      body: () => <LoanDocumentIssuanceSection dealId={deal.id} dealCreatedAt={deal.createdAt} />,
    },
    {
      num: 8,
      title: "Approval / Rejection Letter",
      body: () => <ApprovalRejectionLetterSection dealId={deal.id} />,
    },
    {
      num: 9,
      title: "Closing Coordinator + Hand-off",
      body: () => <ClosingCoordinatorAssignmentSection dealId={deal.id} />,
    },
  ];

  return (
    <div
      className="flex flex-col gap-space-2"
      data-testid="underwriting-tab-content"
    >
      {sections.map((s) => {
        const unlocked = state.unlocked[s.num];
        const status = sectionStatus(s.num, state);
        const expanded = openSection === s.num && unlocked;
        return (
          <section
            key={s.num}
            className="border border-border-light rounded-md bg-white-pure"
            data-testid={`uw-section-${s.num}`}
            data-status={status}
          >
            <button
              type="button"
              className={`w-full flex items-center justify-between gap-space-3 px-space-4 py-space-3 text-left ${
                unlocked ? "cursor-pointer hover:bg-white-warm" : "cursor-not-allowed"
              }`}
              onClick={() => unlocked && setOpenSection(expanded ? 0 : s.num)}
              aria-expanded={expanded}
              aria-controls={`uw-section-body-${s.num}`}
              disabled={!unlocked}
              title={
                unlocked
                  ? undefined
                  : `Section ${s.num - 1} must be marked Complete first.`
              }
            >
              <div className="flex items-center gap-space-3 min-w-0">
                {unlocked ? (
                  expanded ? (
                    <ChevronDown size={16} aria-hidden />
                  ) : (
                    <ChevronRight size={16} aria-hidden />
                  )
                ) : (
                  <Lock size={14} className="text-gray-mid" aria-hidden />
                )}
                <span className="font-display text-h4 text-onyx">
                  Section {s.num} · {s.title}
                </span>
              </div>
              <SectionStatusBadge status={status} />
            </button>
            {expanded && (
              <div
                id={`uw-section-body-${s.num}`}
                className="border-t border-border-light p-space-3"
              >
                {s.body()}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function sectionStatus(
  num: number,
  state: ReturnType<typeof useUnderwritingTabState>,
): SectionStatus {
  if (!state.unlocked[num]) return "Locked";
  const complete = [
    state.section1Complete,
    state.section2Complete,
    state.section3Complete,
    state.section4Complete,
    state.section5Complete,
    state.section6Complete,
    state.section7Complete,
    state.section8Complete,
    state.section9Complete,
  ][num - 1];
  if (complete) return "Complete";
  // For simplicity (and to avoid a second backend round-trip) we treat
  // unlocked-but-incomplete as Open and let the cert checkbox states convey
  // progress inline.
  return "Open";
}
